import { T } from '@design/index';
import type { CSSProperties } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// PatientUIGovernor — Single enforcement layer for ALL patient-web pages
// Import from: '../../ui/PatientUIGovernor'
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1. Layout Rules ────────────────────────────────────────────────────────

export const GOV_LAYOUT = {
  desktop: {
    maxWidth: 1280,
    padding: '0 24px 100px',
  },
  mobile: {
    maxWidth: '100%',
    padding: '0 16px 80px',
  },
  paddingScale: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
    xxxl: 32,
  },
} as const;

export function govPageContainer(overrides?: CSSProperties): CSSProperties {
  return {
    maxWidth: GOV_LAYOUT.desktop.maxWidth,
    width: '100%',
    margin: '0 auto',
    padding: GOV_LAYOUT.desktop.padding,
    fontFamily: T.font,
    ...overrides,
  };
}

// ─── 2. Typography Rules ────────────────────────────────────────────────────

export const GOV_TYPE = {
  pageTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: T.text,
    fontFamily: T.font,
    lineHeight: 1.3,
    margin: 0,
  } as CSSProperties,

  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: T.text,
    fontFamily: T.font,
    lineHeight: 1.3,
    margin: 0,
  } as CSSProperties,

  body: {
    fontSize: 13,
    fontWeight: 400,
    color: T.textSec,
    fontFamily: T.font,
    lineHeight: 1.5,
  } as CSSProperties,

  caption: {
    fontSize: 11,
    fontWeight: 400,
    color: T.textTer,
    fontFamily: T.font,
    lineHeight: 1.5,
  } as CSSProperties,
} as const;

// ─── 3. Card System ─────────────────────────────────────────────────────────

export const GOV_CARD = {
  base: {
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    padding: 14,
    boxShadow: T.shadow,
  } as CSSProperties,

  interactive: {
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
    padding: 14,
    boxShadow: T.shadow,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  } as CSSProperties,

  hoverLift: {
    transform: 'translateY(-3px)',
    boxShadow: T.shadowMd,
  } as CSSProperties,

  hoverReset: {
    transform: 'none',
    boxShadow: T.shadow,
  } as CSSProperties,
} as const;

export function govCardHover(hovered: boolean, accentColor?: string): CSSProperties {
  if (!hovered) return {};
  return {
    transform: 'translateY(-3px)',
    boxShadow: accentColor
      ? `0 8px 24px ${accentColor}18`
      : T.shadowMd,
    borderColor: accentColor ? `${accentColor}50` : T.teal,
  };
}

// ─── 4. Compliance Text ─────────────────────────────────────────────────────

export const GOV_COMPLIANCE = {
  text: {
    fontSize: 9,
    fontWeight: 400,
    color: T.textTer,
    fontFamily: T.font,
    lineHeight: 1.6,
    textAlign: 'center',
  } as CSSProperties,

  inline: {
    fontSize: 9,
    color: T.textTer,
    fontFamily: T.font,
    lineHeight: 1.6,
    textAlign: 'center',
    marginTop: 12,
  } as CSSProperties,

  footer: {
    fontSize: 9,
    fontWeight: 400,
    color: T.textTer,
    fontFamily: T.font,
    lineHeight: 1.6,
    textAlign: 'center',
    marginTop: 20,
    padding: '14px 16px',
    background: T.white,
    border: `1px solid ${T.border}`,
    borderRadius: 14,
  } as CSSProperties,
} as const;

// ─── 5. Grid System ─────────────────────────────────────────────────────────

export const GOV_GRID_CLASS = 'nhc-gov-grid';
export const GOV_MARKET_GRID_CLASS = 'nhc-market-grid';

export const GOV_GRID = {
  standard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
  } as CSSProperties,

  recommendations: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: 14,
  } as CSSProperties,
} as const;

export const GOV_GRID_CSS = `
  .${GOV_GRID_CLASS} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  @media (min-width: 768px) {
    .${GOV_GRID_CLASS} { grid-template-columns: repeat(3, 1fr); }
  }
  @media (min-width: 1024px) {
    .${GOV_GRID_CLASS} { grid-template-columns: repeat(4, 1fr); }
  }
  .${GOV_GRID_CLASS} > * { height: 100%; }

  .${GOV_MARKET_GRID_CLASS} {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  @media (min-width: 768px) {
    .${GOV_MARKET_GRID_CLASS} { grid-template-columns: repeat(3, 1fr); }
  }
  @media (min-width: 1024px) {
    .${GOV_MARKET_GRID_CLASS} { grid-template-columns: repeat(4, 1fr); }
  }
  .${GOV_MARKET_GRID_CLASS} > * { height: 100%; }
`;

// ─── Enforcement Reference Table ────────────────────────────────────────────
//
// Rule                     | Value               | Scope
// ─────────────────────────|─────────────────────|────────────────────────
// Layout maxWidth desktop  | 1280px              | ALL pages
// Layout maxWidth mobile   | 100%                | ALL pages
// Padding desktop          | 0 24px 100px        | ALL pages
// Padding mobile           | 0 16px 80px         | ALL pages
// pageTitle                | 18px / 800          | ALL pages
// sectionTitle             | 15px / 700          | ALL pages
// body                     | 13px / 400          | ALL pages
// caption                  | 11px                | ALL pages
// Card borderRadius        | 14                  | ALL cards
// Card hover lift          | translateY(-3px)    | Interactive cards
// Card shadow              | T.shadow            | ALL cards
// Card hover shadow        | T.shadowMd or tinted| Interactive cards
// Compliance text          | 9px / T.textTer     | ALL compliance text
// Grid mobile              | 2 columns           | ALL grids
// Grid tablet (768px)      | 3 columns           | ALL grids
// Grid desktop (1024px)    | 4 columns           | Standard grids
// Market grid desktop      | 4 columns           | CareServicesHub only
//
